import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../../components/ConfirmModal';
import { usuarioService } from '../../../services/usuarioService';
import { perfilService } from '../../../services/perfilService';

const UsuarioEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [form, setForm] = useState({
    nome: '',
    username: '',
    email: '',
    perfil_id: '',
    ativo: 1
  });
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    loadUsuario();
    loadPerfis();
  }, [id]);

  const loadUsuario = async () => {
    try {
      setLoading(true);
      const response = await usuarioService.getById(id);
      setForm({
        nome: response.data.nome,
        username: response.data.username || '',
        email: response.data.email,
        perfil_id: response.data.perfil_id,
        ativo: response.data.ativo
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPerfis = async () => {
    try {
      const response = await perfilService.list();
      setPerfis(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar perfis:', err);
    }
  };

  const validate = () => {
    if (!form.nome.trim()) return 'O nome é obrigatório.';
    if (form.nome.length > 255) return 'O nome deve ter até 255 caracteres.';
    
    if (!form.email.trim()) return 'O email é obrigatório.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) return 'Email inválido.';
    
    if (!form.perfil_id) return 'Selecione um perfil.';
    
    return '';
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      setShowConfirm(false);
      setSaving(true);
      setError('');
      
      const userData = {
        nome: form.nome,
        email: form.email,
        perfil_id: parseInt(form.perfil_id),
        ativo: form.ativo
      };
      
      await usuarioService.update(id, userData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/seguranca/usuarios');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Erro ao atualizar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setShowDeleteModal(false);
      setSaving(true);
      setError('');
      
      await usuarioService.delete(id);
      setSuccess(true);
      setTimeout(() => {
        navigate('/seguranca/usuarios');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Erro ao excluir usuário');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-lg mx-auto mt-10 text-blue-600">Carregando dados...</div>;

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Usuário</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            maxLength={255}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Nome completo do usuário"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Username</label>
          <input
            type="text"
            name="username"
            value={form.username}
            disabled
            className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-600 cursor-not-allowed"
            placeholder="Username não pode ser alterado"
          />
          <small className="text-gray-500">O username não pode ser modificado após criação</small>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Email *</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="email@exemplo.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Perfil *</label>
          <select
            name="perfil_id"
            value={form.perfil_id}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Selecione um perfil</option>
            {perfis.map(perfil => (
              <option key={perfil.id} value={perfil.id}>
                {perfil.nome} - {perfil.descricao}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="ativo"
                value={1}
                checked={form.ativo === 1}
                onChange={(e) => setForm(prev => ({ ...prev, ativo: parseInt(e.target.value) }))}
                className="mr-2"
              />
              Ativo
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="ativo"
                value={0}
                checked={form.ativo === 0}
                onChange={(e) => setForm(prev => ({ ...prev, ativo: parseInt(e.target.value) }))}
                className="mr-2"
              />
              Inativo
            </label>
          </div>
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {saving && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Usuário atualizado com sucesso!</div>}
        
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={() => navigate('/seguranca/usuarios')}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={() => setShowDeleteModal(true)}
          >
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">Excluir</span>
          </button>
          <button 
            type="submit" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
           aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
        </div>
      </form>
      
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Alteração"
        message="Deseja realmente atualizar este usuário?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
      />
      
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o usuário "${form.nome}"? Esta ação não pode ser desfeita.`}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default UsuarioEdit;
